<template>

  <section>

    <!-- XOL-596 (C-8): after a 428 and "Not now", text and field changes are not
         being saved until the page is reloaded. Renders nothing otherwise. -->
    <out-of-date-banner />

    <!-- XOL-371: the "view only" banner is shown ONLY to actual view-only users
         (viewer=true) — they are the sole Tower Co users with no edit access to
         anything. Regular / manager / approver users may still upload files and
         edit non-role-gated fields even when they cannot action workflow steps,
         so the previous !canWriteStep() condition mislabelled them as view-only. -->
    <el-row v-if="isViewOnly()">
      <el-col :span="24">
        <el-alert
          type="info"
          title="View only — you do not have permission to make changes to this request."
          show-icon
          :closable="false" />
      </el-col>
    </el-row>

    <!-- XOL-526: shown only to a backhaul provider viewing its own RTB (never
         the Tower Co, the telco owner, or a Backhaul org that IS organisation_id).
         S3 (edit) appears in the body only when isCounterpartyWriter() is true
         for this viewer — see providerBannerBody(). -->
    <el-row v-if="isProviderOnlyViewer()">
      <el-col :span="24">
        <el-alert
          type="info"
          :title="'You are viewing this ' + actionData.actionType + ' as its backhaul provider.'"
          show-icon
          :closable="false">
          <span>{{providerBannerBody()}}</span>
        </el-alert>
      </el-col>
    </el-row>

    <el-row>
      <el-col :span="24">
        <!-- XOL-586: `emails` can be null (a request row whose column was never filled): there is
             no switch to show then, and reading `.sendMail` of null threw on every open. -->
        <el-alert v-if="requestMailOff()"
          type="info"
          :title="'This ' + actionData.actionType + ' won\'t send e-mails'"
          description=""
          show-icon
          :closable="false">
            In the email settings, send email has been disabled. A manager level user can turn it on.&nbsp;
            <!-- XOL-596: a content field: saved at the form pause, not on this change. -->
            <el-switch v-if="isTowerCo() && isManager()" v-model="actionData.emails.sendMail" @change="fieldChanged(actionData, 'emails.sendMail')" />
          </el-alert>
        </el-col>

        <el-row>

          <el-col :span="24" align="center">

            <h4 :class="titleColour()">
              <small><i :class="actionData.workflow.icon"></i> {{actionData.workflow.name}}:</small>
               {{actionData.site.siteName}}
            </h4>

            <span v-if="actionData.organisation.colourCode && actionData.organisation.shortName">
              <org-badge class="pull-right" :organisation="actionData.organisation" />
            </span>
            <div v-else>
              <br />

              <span class="text-muted"><small>by: <b>{{actionData.organisation.name}}</b></small></span>
            </div>

          </el-col>
        </el-row>

        <el-row>
          <el-col v-if="isTablet || isDesktop" :span="24">
            <el-steps :active="activeStep-1" finish-status="success" align-center
            :direction="!(isTablet || isDesktop) ? 'vertical' : 'horizontal'">

              <el-step v-for="s in steps" :key="s.title" :description="s.title">
                <template #icon>
                  <i :class="s.icon" style="background: inherit;"></i>
                </template>
              </el-step>  <!-- :title="s.title" -->

            </el-steps>
          </el-col>

          <el-row>
              <el-col :span="24">

                <!-- XOL-393: default to the VIEWER's own party. With no v-model the
                     party tabs always opened on party 1 (the Tower Co), so a telco
                     opening a request saw the Tower Co's files and — because the panes
                     are `lazy` — its own pane was not rendered at all until clicked.
                     Panes need an explicit :name or Element UI names them positionally
                     ("0","1") and the partyNo model could never match. -->
                <el-tabs type="border-card" v-model="selectedPartyTab">

                  <el-tab-pane  v-for="partyNo in actionData.parties" :key="partyNo" :name="String(partyNo)" :label="partyTabLabel(partyNo)" lazy>

                    <el-tabs v-model="selectedFileTab" @tab-click="onFileTabClick">
                      <el-tab-pane  v-for="(s, ind) in steps" :name="'fileStep' + Number(ind + 1)" :key="s.id" :disabled="reachedStep < Number(ind + 1)" lazy>
                        <template #label><i :class="s.icon">&nbsp;</i><span v-if="isDesktop">{{s.title}}</span></template>

                          <files-table
                            :title="s.title + (!partyOrg(partyNo) ? ': ' + partyName(partyNo) : '')"
                            :id="getFileTableRef(partyNo)"

                            :name="getFileTableRef(partyNo)"
                            :table-ref="'filestable' + partyNo"
                            :org-badge="partyOrg(partyNo)"

                            :site-id="actionData.site_id"
                            :type="actionData.actionType.toLowerCase()"
                            :type-id="actionData.id"
                            :step="ind + 1"
                            :partyNo="partyNo"
                            :step-data="step"
                            show-refresh
                            :show-search="false"
                            :disable-form="!isActive(actionData)"
                            size="small"
                            :upload-info="requestsInfo"
                            :upload-callback="afterUpload"
                            :upload-locked="isStepFrozen(ind + 1)"
                            :allow-add-category="!isStepFrozen(ind + 1) && (isTowerCo() || (Number(partyNo) === viewerPartyNo() && (ind + 1) === activeStep && !partyOneStepCompleted(ind + 1) && isActive(actionData)))"
                            />

                          <!-- XOL-596 (completed steps, CS-F): the file tabs sit at the top of the
                               drawer, the step hint inside the Workflow collapse (often closed), and a
                               disabled Upload's tooltip can't be reached by keyboard or touch. -->
                          <p v-if="isStepFrozen(ind + 1) && isActive(actionData)" class="text-muted step-completed-files">
                            <small><i class="fa fa-lock" aria-hidden="true"></i> {{completedFilesNote(ind + 1)}}</small>
                          </p>

                    </el-tab-pane>
                    </el-tabs>

                    <!-- XOL-526 (D3 files; UX §1.7(b)): the Telco's lighter equivalent
                         of the provider banner — makes the party-2 sharing visible
                         without a banner on every viewer. -->
                    <p v-if="Number(partyNo) === 2 && hasDistinctProvider()" class="text-muted">
                      <small><i class="fa fa-users" aria-hidden="true"></i> {{sharedFilesCue()}}</small>
                    </p>

                  </el-tab-pane>
                </el-tabs>
            </el-col>

        </el-row>

        <br />

        </el-row>

        <el-collapse v-model="show" accordion>
          <el-collapse-item name="map">
            <template #title>
                <span class="text-info">Map&nbsp;<i class="header-icon fa fa-map-marker"></i></span>
              </template>
              <site-map :row-data="actionData" />
          </el-collapse-item>


          <el-collapse-item name="workflow">
            <template #title>
                <span class="text-info">Workflow&nbsp;<i class="header-icon fa fa-ellipsis-h"></i></span>
              </template>

              <el-row>
                <el-col :offset="1" :span="21">
                <center><h4 v-if="steps[activeStep-1]"><i :class="steps[activeStep-1].icon"></i> {{steps[activeStep-1].title}}</h4> <small class="text-muted">| Step {{activeStep}} of {{steps.length}}</small></center>
                <br />

             <!-- XOL-596 (completed steps, CS-H): ONE line when the active step is complete,
                  for every viewer (it replaces XOL-526's counterparty-only alert, UX U-G, with
                  the same look): the step's form is read-only, who completed it, and how to
                  re-open it. role="status" overrides the alert's role="alert", so it is not
                  read out assertively every time a completed step opens; tabindex -1 lets a
                  freeze that disabled the focused control move focus here. Never inside
                  .approval-collapse: steps 1 and 4 have no approval section. -->
             <el-row v-if="isStepFrozen(activeStep) && isActive(actionData)">
               <el-col :span="24">
                 <el-alert
                   :id="hintId"
                   class="step-completed-hint"
                   tabindex="-1"
                   role="status"
                   aria-live="polite"
                   type="success"
                   show-icon
                   :closable="false"
                   :center="!isMediumOrLess"
                   :title="completedHint" />
               </el-col>
             </el-row>

             <el-row :gutter="16">
               <el-col :span="22" :offset="2">
                 <el-form :model="state">
                   <component :is="comp" @updated="stepUpdated" :row-data="rowData" :row-index="rowIndex" :step-data="step" :read-only="readOnly" />
                </el-form>
                </el-col>
               </el-row>

                 <!-- XOL-607: the footer was three floated el-cols shifted with push / pull. Those
                      move the PAINT position but not the flow box, so the Finish column (later in the
                      DOM, so painted on top) sat over the Complete Step box: all of it at step 1, its
                      right part from step 2 on, and the "Complete Step" label could not be clicked
                      (only the checkbox square, which Element lifts with its own z-index). Now one flex
                      row of three cells in DOM order, no positioning, so no box overlaps another.
                      The Previous cell is always rendered (empty at step 1) so the Complete box keeps
                      the same place at every step; stacked (xs / sm / md) it is hidden when empty. -->
                 <div v-if="isTowerCo()" class="workflow-footer" :class="{ 'workflow-footer--stacked': isMediumOrLess }">

                   <div class="workflow-footer__prev" :class="{ 'workflow-footer__prev--empty': activeStep <= 1 }">
                      <el-button v-if="activeStep > 1" type="text" @click="prevStep()">&lt;&nbsp;Previous Step</el-button>
                   </div>

                   <div class="workflow-footer__complete">
                     <el-alert show-icon :type="completeBox.done ? 'success' : 'warning'" center :closable="false">
                       <check-box v-if="isTowerCo() && isManager()" class="complete-step-box" v-model="step.completed" no-form @change="onCompleteToggle" :disabled="!canWriteStep() || !isActive(actionData) || savingStep" :aria-busy="savingStep ? 'true' : null">Complete Step</check-box>
                       &nbsp;<cmd-saving v-if="savingStep" :busy="true" /><span v-else-if="completeBox.done">on <b>{{formatDateTime(completeBox.at)}}</b> by <b>{{completeBox.by}}</b></span>
                       <span v-else>Step Pending Approval</span>
                     </el-alert>
                   </div>

                   <div class="workflow-footer__finish">
                     <!-- XOL-596 (UX 11, 13): Finish is navigation only (it writes
                          nothing), so it has no :loading; it is disabled while a
                          command runs. The hint says what unlocks it. -->
                     <el-button v-if="activeStep <= steps.length" type="info" @click="nextStep()" :disabled="!canWriteStep() || !validateStep(activeStep) || savingStep" :aria-disabled="!canWriteStep() || !validateStep(activeStep) || savingStep" aria-describedby="finish-step-hint">Finish Step <b>{{activeStep}}</b> / {{steps.length}}</el-button>
                     <el-button v-if="isTowerCo() && activeStep === steps.length && step.approved" type="success" @click="completeWorkflow()" :loading="completingRequest" :disabled="!canWriteStep() || !validateStep(activeStep) || savingStep">{{completingRequest ? completingLabel : 'Complete ' + actionData.actionType}}</el-button>
                     <div v-if="finishHint" class="workflow-footer__hint"><small class="text-muted" id="finish-step-hint">{{finishHint}}</small></div>
                   </div>
                 </div>

            </el-col>
          </el-row>
        </el-collapse-item>

  </el-collapse>
  </el-row>

  <el-row>
    <el-col :span="4">
      <small class="text-muted">{{actionData.actionType}} # <b>{{actionData.id}}-{{step.partyNo}}-{{step.stepNo}}/{{step.id}}</b></small>
    </el-col>

    <el-col :span="20" align="right">

      <!-- XOL-586 (F1, D-2026-10-16b): Emails Update is for the Tower Co only. An owner
           or a provider gets no popup at all, not even a read-only one, and there is no
           red "Emails Setup" variant: the lists are live, so there is nothing to set up.
           A view-only Tower Co user sees the trigger disabled (Popup.vue).

           Activity (NEW-B, F3) sits to the LEFT of Emails Update, for the Tower Co only too.
           It is read-only, so a view-only Tower Co user may open it (Q9): `allow-view-only`. -->
      <div v-if="isTowerCo()" class="request-footer-links">
        <popup :heading="activityHeading()"
          button-text="Activity"
          button-style="text"
          :show-save="false"
          allow-view-only
          button-icon="fa fa-history"
          @open="reloadActivity">

          <request-activity-log ref="activity" :row-data="actionData" />

        </popup>

        <popup v-if="showEmailSetup" heading="Configure Emails"
          button-text="Emails Update"
          button-style="text"
          :show-save="false"
          :center-button="true"
          button-icon="fa fa-envelope"
          @open="reloadEmailSetup">

        <email-setup ref="emailSetup" :row-data="actionData" />

      </popup>
    </div>
  </el-col>

  </el-row>

</section>
</template>


<script>


import RequestMixins from '@/mixins/RequestMixins'
import DrawerBusyMixin from '@/mixins/DrawerBusyMixin'

import * as sync from '@/lib/requestRowSync'
import { copyText } from '@/lib/requestSyncCopy'
import { waitForSaves, keepTrying } from '@/lib/saveWait'
import * as commandNotices from '@/lib/commandNotices'
import * as savePopup from '@/lib/savePopup'
import * as approvalLock from '@/lib/approvalLock'
import { isLogoutPath } from '@/lib/leaveGuard'
import { closedWho } from '@/lib/syncNotices'
import { onStepCompleted } from '@/lib/stepCompleted'
import { announceInDrawer } from '@/lib/liveRegion'
import { isTearingDown } from '@/lib/session'
import { parentPath } from '@/lib/layerNav'

import EmailSetup from '@/components/requests/EmailSetup'
import RequestActivityLog from '@/components/requests/RequestActivityLog'
import { activityText, escapeHtml } from '@/lib/requestActivity'
import CmdSaving from '@/components/requests/CmdSaving'
import OutOfDateBanner from '@/components/requests/OutOfDateBanner'
import FilesTable from '@/components/tables/FilesTable'

import step1 from '@/components/requests/workflow/step1'
import step2 from '@/components/requests/workflow/step2'
import step3 from '@/components/requests/workflow/step3'
import step4 from '@/components/requests/workflow/step4'
import step5 from '@/components/requests/workflow/step5'

import SiteMap from '@/components/requests/SiteMap'


export default {
  // No DrawerDirtyMixin: since XOL-596 every content field saves itself, per
  // field, 3 s after the user's last input in this drawer (the form scope created
  // in provide()), and closing the drawer is a flush point: the close waits for
  // pending saves (the leave guard below) and stays open, with the normal
  // notice, if one fails. Nothing is discarded, so there is nothing to confirm.
  // DrawerBusyMixin DOES apply: while a COMMAND is in flight (`busyCommands`), the
  // drawer must not close (it would race the request) — see `drawerBusy`. A pending or
  // running DECISION save (the approval controls) is not a command: the close is a
  // flush point that waits for it.
  mixins: [RequestMixins, DrawerBusyMixin],

  // XOL-596: the form scope of this drawer. The 3 s pause is form-level: any
  // change or typing anywhere in the drawer (including the e-mail popup, which
  // is a Vue child even though its DOM is appended to <body>) restarts ONE timer.
  // Created here, as a plain instance property (it must not become reactive), and
  // provided to every step component and EmailSetup.
  provide () {
    this.syncScope = sync.createScope('request-drawer')

    // The approvals decided in THIS drawer (step id -> true), for the close prompt:
    // "ONLY if a decision was made in this session". A plain instance property (it
    // must not become reactive); a decision is noted by a succeeding approval
    // command and forgotten by a lock, a Pending, or "Complete without locking".
    this.sessionDecisions = {}

    // The command host (XOL-596 UF2): a step component's running command reaches
    // `drawerBusy` through `busy` (the close veto; NOT the footer: only the footer's
    // own commands grey it); `close` is the drawer closing itself when its request no
    // longer exists.
    var self = this

    return {
      requestSyncScope: this.syncScope,
      // This drawer's own hint id (a per-instance id: two drawers can be mounted), the focus
      // target of a refusal that came from a step component.
      requestHintId: this.hintId,
      requestCommandHost: {
        busy: function (on) {
          self.commandBusy(on, true)
        },
        close: this.closeRequestDrawer,
        noteDecision: this.noteDecision,
        forgetDecision: this.forgetDecision
      }
    }
  },

  components: {
    CmdSaving,
    OutOfDateBanner,
    EmailSetup,
    RequestActivityLog,
    FilesTable,
    step1,
    step2,
    step3,
    step4,
    step5,
    SiteMap
  },

  props: {

    rowData: {
      type: Object,
      default: null
    },

    rowIndex: {
      type: Number,
      default: null
    },

    closeDialog: {
      type: Function,
      default: null
    }
  },

  methods: {

    // XOL-586: the Emails Update popup opened. The recipient lists are live, so every
    // open re-reads them. The FIRST open has nothing to reload: the drawer renders its
    // body only once it is shown, and EmailSetup fetches in its own `mounted()`.
    reloadEmailSetup () {
      var setup = this.$refs.emailSetup

      if (setup && typeof setup.reload === 'function') {
        setup.reload()
      }
    },

    // XOL-586 (NEW-B): "Activity: RTB #1234", the Activity popup's title.
    activityHeading () {
      return activityText('requestActivity.heading', { rtx: this.actionData.actionType, id: this.actionData.id })
    },

    // The Activity popup opened. The FIRST open has nothing to reload: the drawer renders its
    // body only once it is shown, and the log reads in its own `mounted()`. Every later open
    // reads page 1 again, with the filters the user left.
    reloadActivity () {
      var log = this.$refs.activity

      if (log && typeof log.reload === 'function') {
        log.reload()
      }
    },

    // XOL-596 (UF2): a command running here or in a step component. Two counts
    // (decision fields, D11):
    //   busyCommands  every explicit command in the drawer, own or a child's (reject,
    //                 lock, unlock, Complete Step ...): drives `drawerBusy`, the close
    //                 veto, because a command POST must not race the close;
    //   ownCommands   this component's OWN commands only (complete, re-open, Lock &
    //                 complete, Complete {RTx}): drives `savingStep`, which greys the
    //                 footer. A decision save, a reject or a lock-line command does not.
    commandBusy (on, child) {
      this.busyCommands = Math.max(0, this.busyCommands + (on ? 1 : -1))

      if (!child) {
        this.ownCommands = Math.max(0, this.ownCommands + (on ? 1 : -1))
        this.savingStep = this.ownCommands > 0
      }
    },

    // The drawer closing itself (a command found its request gone): the same
    // thing the X does, one level up.
    closeRequestDrawer () {
      if (this.closeDialog) {
        this.closeDialog()
      } else if (this.$router && this.$route) {
        this.$router.push({ path: parentPath(this.$route.path) })
      }
    },

    // XOL-596 (approval lock): this drawer's session decisions.
    noteDecision (step) {
      if (step && step.id !== undefined && step.id !== null) {
        this.sessionDecisions[step.id] = true
      }
    },

    forgetDecision (step) {
      if (step && step.id !== undefined && step.id !== null) {
        delete this.sessionDecisions[step.id]
      }
    },

    // The approvals the close should ask about: decided in this drawer, still
    // decided, not locked (by us or, meanwhile, by someone else: the adopt shows it),
    // on a live request, and ones this user may lock. Empty for the exits that never
    // ask: logout, a leave the user already gave up their saves for (Leave now), a
    // session teardown, and a request that is no longer open. Recomputed on every
    // call, from the baseline.
    lockCandidates (to) {
      var out = []

      if (isTearingDown() || !this.isActive(this.actionData)) {
        return out
      }

      if (to && (isLogoutPath(to.path) || sync.isLeaveBypassed(to.fullPath))) {
        return out
      }

      var steps = this.actionData.steps || []

      for (var i = 0; i < steps.length; i++) {
        var step = steps[i]

        // A COMPLETED step takes no lock (Carlos, 2026-10-14): never listed, even for a
        // decision made in this session on a step someone else then completed.
        if (!this.sessionDecisions[step.id] || this.approvalLocked(step) || !this.approvalDecided(step) || !this.canLockApproval(step) ||
          this.isStepFrozen(step.stepNo)) {
          continue
        }

        var kind = this.approvalKind(step)
        var base = sync.getBaseline(step)

        out.push({
          step: step,
          kind: kind,
          // The decision the prompt lists is the decision the lock is made against
          // (UF-L2): the API refuses it if someone changed it in between.
          expected: approvalLock.expectedBody(kind, base),
          row: {
            n: step.stepNo,
            title: approvalLock.stepTitle(this.actionData, step),
            decision: approvalLock.decisionText(kind, base, this.actionData),
            date: step.approved_at ? this.formatDate(step.approved_at) : null
          }
        })
      }

      return out
    },

    // Closing the drawer is a flush point (R2.14, R2.18 A). Called by LayerHost
    // before the close, and only when `leaveNeeded` says this drawer has
    // something to do. Resolves true to let it close, false to keep it open.
    //
    // 1. FLUSH. The pending saves go first, with the card reading "Saving your
    //    changes…". This order is required: the lock makes the API refuse remarks,
    //    Revise TP and Re-survey, so a lock sent before the user's last remarks
    //    (still in the 3 s pause) would get them refused. A failed save has already
    //    raised its notice (reason 'leave' adds "This page stays open until your
    //    changes are saved."): false, and no lock prompt. A save that HANGS asks the
    //    user after 15 s (waitForSaves: keep waiting, or leave now: closes with no
    //    prompt). Saves that FAIL on the network keep the close held and retrying
    //    (leaveAfterFailedSave): a retry that succeeds goes on to 2; Close without
    //    saving closes with no prompt.
    // 2. ASK (Carlos, 2026-10-13): approvals decided in this drawer and not locked
    //    (lockCandidates). Lock / Leave unlocked / Stay.
    // 3. LOCK, one /lock per approval in sequence; if any fails the drawer stays open
    //    under one summary.
    //
    // Scoped to THIS drawer: it flushes and waits on this drawer's rows and their
    // in-flight saves, never on another open drawer's fields.
    leaveGuard (to) {
      var self = this
      var pending = sync.hasPendingSaves(this.syncScope, to ? to.fullPath : undefined)

      if (!this.leaveNeeded(to)) {
        return Promise.resolve(true)
      }

      if (!pending) {
        return this.askAndLockOnClose(to)
      }

      // C-12: the save-status card reads "Saving your changes…" at once.
      var end = savePopup.leaving()

      return waitForSaves(this.$root, sync.flushScopeAndWait(this.syncScope, { reason: 'leave' })).then(function (r) {
        end()

        if (r.left) {
          return true
        }

        if (r.result && r.result.ok) {
          return self.askAndLockOnClose(to)
        }

        return self.leaveAfterFailedSave(r.result).then(function (res) {
          return res.close ? (res.saved ? self.askAndLockOnClose(to) : true) : false
        })
      })
    },

    // Synchronous: is anything of THIS drawer left to do? LayerHost asks this before
    // it holds the close, so a close with nothing pending goes straight through, in
    // the same tick: nothing to save and no approval to ask about.
    leaveNeeded (to) {
      // `to`: the navigation LayerHost is asking about, so a leave bypass for that
      // navigation (Log out anyway) can answer false and no other.
      return sync.hasPendingSaves(this.syncScope, to ? to.fullPath : undefined) || this.lockCandidates(to).length > 0
    },

    // 2 + 3 of the close. Resolves true to let the drawer close.
    askAndLockOnClose (to) {
      var self = this
      var candidates = this.lockCandidates(to)

      if (candidates.length === 0) {
        return Promise.resolve(true)
      }

      return approvalLock.askLock(this.$root, {
        mode: 'close',
        rows: candidates.map(function (c) { return c.row }),
        rtx: this.actionData.actionType,
        id: this.actionData.id,
        alive: function () { return !self.syncScope.destroyed }
      }).then(function (answer) {
        // Stay / Esc / the X: the close is refused, nothing is sent.
        if (answer === 'cancel') {
          return false
        }

        // Leave unlocked: close; nothing is sent and nothing is said.
        if (answer === 'skip') {
          return true
        }

        // The prompt has closed (askLock resolves after it): only now do the
        // commands run, so their own dialogs can show.
        return self.lockOnClose(candidates)
      })
    },

    // One /lock per approval, in sequence, quiet; then ONE card message. Resolves
    // true when every approval is locked (the drawer closes).
    lockOnClose (candidates) {
      var self = this
      var action = this.actionData
      var end = savePopup.leaving(copyText('requestSync.approvalLock.locking', { n: candidates.length }))
      var items = []

      var next = function (i) {
        if (i >= candidates.length) {
          return Promise.resolve()
        }

        var c = candidates[i]

        return self.lockCommand(c.step, { quiet: true, expected: c.expected, ordering: 'lockOnClose' }).then(function (out) {
          items.push({ step: c.step, out: out })

          if (out.ok) {
            self.forgetDecision(c.step)
          }

          // A request that closed under us ends the run: its own notice, no summary.
          return out.status === 'closed' ? Promise.resolve() : next(i + 1)
        })
      }

      return next(0).then(function () {
        end()

        var failed = items.filter(function (i) { return !i.out.ok })
        var ctx = { root: self.$root, action: action, step: items.length ? items[0].step : null, rtx: action.actionType }

        if (failed.length === 0) {
          var steps = items.map(function (i) { return i.step.stepNo })
          var prefix = copyText('requestSync.saved.prefix', { rtx: action.actionType, id: action.id })

          savePopup.success(commandNotices.lockDoneMessage('lock', { step: items[0].step, steps: steps, prefix: prefix }).message)

          return true
        }

        if (failed.some(function (i) { return i.out.status === 'closed' })) {
          savePopup.notify(commandNotices.closedNotice(ctx))

          return false
        }

        // A remarks save that failed first has already said (with the "wasn't
        // locked" footer): no second section about the same thing.
        var silent = ['content-not-saved', 'cancelled', 'blocked', 'version-required']

        if (!failed.every(function (i) { return silent.indexOf(i.out.status) !== -1 })) {
          savePopup.notify(commandNotices.closeLockNotice(items, ctx))
        }

        return false
      }, function (e) {
        end()
        console.error('XOL-596: locking the approvals on close threw', e)

        return false
      })
    },

    // A close blocked by a failed save (XOL-596; Carlos, 2026-10-08: "ask the user
    // after 30 seconds of failure to see if they would like to keep trying or
    // close the window"). Called with the flush result when the saves settled and
    // at least one failed; resolves { close, saved }: close true lets the drawer
    // close anyway, and `saved` says the pending fields WERE saved (a retry
    // succeeded), so the close may go on to ask about the approval lock.
    //
    // A CONNECTION PROBLEM (every failed save is a network failure or a 5xx): the
    // drawer stays open under the normal "connection problem" notice, the failed
    // fields are retried on a back-off, a retry that succeeds lets the close
    // through (with the normal success message), and after 30 s of continuous
    // failure the user is asked: Keep trying (asked again after another 30 s) or
    // Close without saving (the pending fields are dropped, nothing is sent later).
    // Anything else (409, 403, 422, ...): close false, the drawer stays open under its
    // own notice, as before. Hung saves keep their own 15 s dialog (leaveGuard).
    leaveAfterFailedSave (result) {
      var self = this

      return keepTrying(this.$root, result, function () {
        return sync.flushScopeAndWait(self.syncScope, { reason: sync.REASON_KEEP_TRYING })
      }, {
        pending: function () { return sync.pendingFields({ scope: self.syncScope }) },
        abandon: function () { sync.discardUnsaved({ scope: self.syncScope }) },
        alive: function () { return !self.syncScope.destroyed }
      })
    },

    // R2.15: a drawer left open in a background tab refreshes when the window
    // comes back, at most every 30 s. Held fields keep their text and get a cue.
    refetchOnFocus () {
      var rec = sync.getRecord(this.actionData)

      if (this.syncScope.destroyed || (rec && Date.now() - rec.adoptedAt < 30000)) {
        return
      }

      var self = this
      var wasActive = this.isActive(this.actionData)

      this.refetchActionById(this.actionData.id, this.actionData).then(function (fresh) {
        if (fresh && wasActive && !self.isActive(self.actionData)) {
          // Who closed it and when, from the rows the re-GET just adopted.
          var who = closedWho(self.$root, self.actionData)

          savePopup.notify({
            type: 'info',
            title: copyText('requestSync.closed.awayTitle', { status: who.status }),
            message: copyText('requestSync.closed.away', { status: who.status, by: who.by, date: who.date }),
            duration: 5500
          })
        }
      })
    },

    onWindowFocus () {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        this.refetchOnFocus()
      }
    },

    getFileTableRef (pNo) {
      return 'filestable' + this.activeStep + '-' + pNo
    },

    // XOL-107/XOL-344/XOL-526: is the party-1 (Tower Co) step for stepNo
    // complete? The check itself moved to RequestMixins.partyOneStepCompleted
    // (step1-5 and other request surfaces need it too) — this delegates so
    // the existing `:upload-locked="partyStepCompleted(...)"` binding is
    // untouched.
    partyStepCompleted (stepNo) {
      return this.partyOneStepCompleted(stepNo)
    },

    // XOL-596 (completed steps, CS-F): the files note under a frozen step's file tab.
    completedFilesNote (n) {
      return copyText('requestSync.completed.files', { n: n })
    },

    // A RECORD-level refusal (403 step_completed on a file action, equipment, a backhaul
    // line, Create Sharer; lib/stepCompleted): its body carries only the step number, so the
    // request is re-read FIRST (the step freezes on screen, and the completed party-1 row
    // says who and when), THEN the C-12 card says why. If the re-GET fails the lead has no
    // name. The editor that was refused stays open with the user's values.
    // Does a refusal reported by `scope` come from THIS request? Several request drawers can
    // be mounted (a request drawer over a site drawer, RequestTable detail rows, RequestAction):
    // only the matching one re-reads and says why. A step context matches by one of this
    // request's step rows, a file by its request id, and a report with neither is anyone's.
    acceptsRefusal (scope) {
      var steps = (this.actionData && this.actionData.steps) || []

      if (scope && scope.actionStepId !== undefined && scope.actionStepId !== null) {
        return steps.some(function (s) { return Number(s.id) === Number(scope.actionStepId) })
      }

      if (scope && scope.actionId !== undefined && scope.actionId !== null && !isNaN(scope.actionId)) {
        return Number(scope.actionId) === Number(this.actionData.id)
      }

      return true
    },

    onStepCompletedRefusal (info) {
      var self = this
      var n = info.stepNo

      if (!n) {
        return
      }

      var say = function () {
        if (self._isDestroyed) {
          return
        }

        var p1 = self.partyOneStepRow(n)
        var completed = !!p1 && !!sync.baselineValue(p1, 'completed')

        savePopup.notify(commandNotices.stepCompletedNotice(self.$root, {
          n: n,
          what: info.what,
          kind: 'change',
          p1: completed ? p1 : null
        }))
      }

      this.refetchActionById(this.actionData.id, this.actionData).then(say, say)
    },

    // A freeze (or a re-open) that came from an ADOPT (another user's completion, a
    // refusal's rows), not from this drawer's own command. The step's pending changes are
    // sent at once, a polite announcement is made for the step on screen, and focus
    // moves to the step hint if the control the user was in has just been disabled.
    onStepFroze (n, active) {
      var self = this

      if (this.ownFreeze[n]) {
        return
      }

      // C8: freezing is a flush point for that step. A pending change is sent now (and
      // refused with the rows) instead of at the next pause. No-op when nothing changed.
      var flushing = sync.flushRows(this.rowsOfStep(n).concat([this.actionData]), { reason: 'pause' })

      if (flushing && typeof flushing.catch === 'function') {
        flushing.catch(function (e) {
          console.error('XOL-596: flushing a step that was just completed threw', e)
        })
      }

      if (n !== this.activeStep) {
        return
      }

      var p1 = this.partyOneStepRow(n)

      announceInDrawer(copyText('requestSync.completed.announce.frozen', {
        n: n,
        by: p1 ? sync.baselineValue(p1, 'completed_by') : null
      }))

      this.$nextTick(function () {
        var el = typeof document !== 'undefined' ? document.getElementById(self.hintId) : null

        if (el && typeof el.focus === 'function' && self.focusWasDisabled(active)) {
          el.focus()
        }
      })
    },

    onStepReopened (n) {
      if (this.ownFreeze[n] || n !== this.activeStep) {
        return
      }

      announceInDrawer(copyText('requestSync.completed.announce.reopened', { n: n }))
    },

    // Was the element that had focus when the step froze taken away or disabled by it? The
    // Complete Step box stays enabled, so the user's own completion never moves focus.
    focusWasDisabled (el) {
      if (!el || el === document.body || el === document.documentElement) {
        return false
      }

      if (!document.body.contains(el)) {
        return true
      }

      var node = el

      while (node && node.nodeType === 1) {
        if (node.disabled || (node.hasAttribute && node.hasAttribute('disabled')) ||
          (node.classList && node.classList.contains('is-disabled'))) {
          return true
        }

        node = node.parentNode
      }

      return false
    },

    // XOL-526 (UX §2.1): the provider banner body, up to four sentences.
    // S3 (edit) only appears when isCounterpartyWriter() is true for this viewer.
    providerBannerBody () {
      var sentences = []

      sentences.push('Files you upload go to the ' + this.partyTabLabel(2) +
        ' tab and are labelled with your organisation. ' + this.actionData.organisation.name +
        ' and ' + this.towerCoName() + ' can see them, and you can see theirs.')

      if (this.actionData.emails && this.actionData.emails.sendMail) {
        sentences.push('Each upload is e-mailed to the other organisations on this request. You receive these notices if Upload Alerts is on in your User Profile.')
      }

      if (this.isCounterpartyWriter()) {
        sentences.push('You can also edit the shared remarks and the Technical Proposal type.')
      }

      sentences.push(this.towerCoName() + ' manages the workflow steps.')

      return sentences.join(' ')
    },



    validateStep (stepNo) {

      let moveOn = false

      switch (stepNo) {
        case 1:
          moveOn = this.step.completed
          break
        case 2:
          moveOn = this.step.completed // && this.step.approved
          break
        case 3:
          moveOn = this.step.completed
          break
        case 4:
          moveOn = this.step.completed
          break
        case 5:
          moveOn = this.step.completed
          break
        default:
          moveOn = false
          break
      }

      return moveOn
    },



    // XOL-596 (B6): Finish Step is NAVIGATION ONLY. The server derives
    // currentStep and status from the completed party-1 steps (there is no Finish
    // endpoint), so there is nothing to write, snapshot or roll back, and no
    // "Couldn't advance step". `validateStep` reads `step.completed`, which only
    // the complete command's adopted response sets. A reopen may lower the
    // request's progress; the view stays where it is.
    nextStep () {

      // A backstop for the footer's own commands (the button is disabled meanwhile).
      if (this.savingStep) {
        return
      }

      if (!this.validateStep(this.activeStep)) {
        return
      }

      if (!this.actionData.site_id || !this.isActive(this.actionData)) {
        return
      }

      var nextActiveStep = this.activeStep + 1

      if (nextActiveStep > this.steps.length) {
        return
      }

      this.activeStep = nextActiveStep
      this.reachedStep = Math.max(nextActiveStep, this.reachedStep)
      this.changeStep(nextActiveStep)
    },



    prevStep () {
      this.activeStep -= 1

      if (this.activeStep <= 0) {
        this.activeStep = 1
      }

      this.changeStep(this.activeStep)
    },


    // XOL-534 (B1): a child step component emits `updated` from its OWN
    // `beforeDestroy()` on every unmount — including when it's the OUTGOING
    // component of a file-tab / step navigation that `changeStep()` already
    // resolved to a DIFFERENT step. Vue mounts the new component (reading
    // `this.step` at the CORRECT, just-set value) before it unmounts the old
    // one, but the old one's stale `updated` emit then reassigns `this.step`
    // — and RequestMixins' `stepData` watcher pushes that corrupted value
    // straight into the already-mounted new child's local `step`. Only
    // accept an emit that actually matches the currently active step/party.
    //
    // XOL-534 round 3 (reviewer correction): `saveStepData(data)` below is
    // still called for parity with the pre-XOL-526 shape of this method, but
    // it is a documented pre-existing NO-OP, not a "steps[] sync" — see
    // saveStepData's own comment and plan §8a (follow-up: delete both).
    stepUpdated (data) {
      if (!data) return

      this.saveStepData(data)

      if (Number(data.stepNo) === this.activeStep && Number(data.partyNo) === this.viewerPartyNo()) {
        this.step = data
      }
    },



    changeStep (stepNo) {

      // XOL-596 (F6): there is no save here any more. It sent the whole step row
      // (workflow state included) on every drawer open at step 2 or later and on
      // every step change, which is how a stale drawer reverted an approval. The
      // outgoing step's content fields save themselves.

      // XOL-393: never let `step` go null — stepData() returns null when no row matches,
      // and the template dereferences step.completed unguarded. That null was the
      // blank-drawer crash.
      //
      // ⚠ The `stepNo - 1` argument here is DEAD. stepData(current = 1) discards it
      // whenever `this.activeStep` is truthy (`if (this.activeStep) current = this.activeStep`),
      // so the lookup always resolves off activeStep, not off this parameter. It reads
      // like a deliberate off-by-one and is not: "correcting" stepData to honour its
      // argument would make changeStep(5) resolve step FOUR's data while displaying
      // step 5. Left as-is rather than changed under this ticket, but do not trust it.
      //
      // This is also why clamping activeStep in created() is what actually fixes the
      // crash — stepData reads that value. The guard below is defence-in-depth.
      var nextStep = this.stepData(stepNo - 1)
      if (nextStep) { this.step = nextStep }

      // XOL-596 (B6): no local `currentStep` bump: progress is the server's.
      if (stepNo > this.reachedStep) { this.reachedStep = stepNo }
      this.selectedFileTab = 'fileStep' + stepNo

      switch (stepNo) {
        case 1:
          this.comp = step1
          break
        case 2:
          this.comp = step2
          break
        case 3:
          this.comp = step3
          break
        case 4:
          this.comp = step4
          break
        case 5:
          this.comp = step5
          break
        default:
          this.comp = step1
      }

    },


    onFileTabClick (pane) {
      const n = Number(String(pane.name || pane.paneName || '').replace('fileStep', ''))

      if (!n || Number.isNaN(n) || n === this.activeStep) {
        return
      }

      if (n > this.reachedStep) {
        return
      }

      this.activeStep = n
      this.changeStep(n)
    },



    // XOL-107/XOL-344: toggling "Complete Step" warns first. Completing locks the
    // step (telco can no longer upload files or make changes); un-ticking
    // re-opens it. XOL-596 (slice 3): after the confirm it is a COMMAND, POST
    // /step/{id}/complete or /reopen; nothing is stamped locally (completed_at,
    // completed_by), the adopt of the response shows who and when. Cancel puts the
    // tick back; so does a failure (the baseline, XOL-40's roll back).
    //
    // XOL-596 (decision fields, D9): ticking COMPLETE first FLUSHES, then asks. A
    // decision still waiting for the pause is saved before the user is asked what to do
    // about its lock, so the prompt names the decision the server holds. The box stays
    // enabled meanwhile; the C-12 card reads "Saving…".
    onCompleteToggle () {
      var self = this
      var complete = !!this.step.completed

      if (complete) {
        // A second TICK while the first one is still being flushed is no new request.
        if (this.completePending) {
          this.step.completed = true
          return
        }

        this.completeAfterFlush(this.step)

        return
      }

      // An UN-tick while the completion still waits for its flush cancels it: the server
      // never completed the step, so there is nothing to re-open and nothing to ask.
      if (this.completePending && this.cancelPendingComplete) {
        this.cancelPendingComplete()

        return
      }

      // XOL-596 (completed steps): Re-open is the one way back to editing, and nothing
      // gates it: not the step's freeze, not an approval lock (the migration's included:
      // the sentence below says it stays locked), not a decision save.
      var step = this.step

      this.$confirm(
        copyText('requestSync.reopenStep.confirm.body', { n: step.stepNo, locked: !!sync.baselineValue(step, 'approvalLocked') }),
        copyText('requestSync.reopenStep.confirm.title'),
        {
          confirmButtonText: copyText('requestSync.reopenStep.confirm.ok'),
          cancelButtonText: copyText('requestSync.reopenStep.confirm.cancel'),
          type: 'warning',
          customClass: 'xol-reload-box'
        }
      ).then(() => {
        self.completeStep(false)
      }, () => {
        self.step.completed = true
      })
    },


    // Wait until no explicit command is running in the drawer (a lock-line Lock /
    // Unlock in flight): the prompt could otherwise offer "Lock & complete" for an
    // approval that is mid-lock.
    //
    // Resolves on its own when the drawer is torn down (the `$watch` dies with it), so the
    // completion waiting on it can let go of its hold instead of leaking it.
    whenCommandsIdle () {
      var self = this

      if (this.busyCommands === 0) {
        return Promise.resolve()
      }

      return new Promise(function (resolve) {
        var waiter = function () {
          unwatch()
          self.idleWaiters = self.idleWaiters.filter(function (w) { return w !== waiter })
          resolve()
        }
        var unwatch = self.$watch('busyCommands', function (n) {
          if (n === 0) {
            waiter()
          }
        })

        self.idleWaiters.push(waiter)
      })
    },


    // Complete Step, ticked: flush the step's pending saves (a pending decision among
    // them), THEN choose the prompt from the baseline as it is now. The tick is held
    // meanwhile, so no response adopted on the way (the decision's) un-ticks it.
    completeAfterFlush (step) {
      var self = this
      var released = false
      var release = function () {
        if (!released) {
          released = true
          sync.releaseOwned(step, ['completed'])
        }
      }
      // This completion's own cancel. A completion that was superseded (un-ticked, then
      // ticked again) still has late branches: they must clear the shared state only
      // while it is theirs, never the state of the completion that replaced them.
      var myCancel = null
      var clearState = function () {
        if (self.cancelPendingComplete === myCancel) {
          self.completePending = false
          self.cancelPendingComplete = null
        }
      }
      var stop = function () {
        release()
        clearState()
        self.revertWorkflowFields(step, ['completed', 'completed_at', 'completed_by'])
        self.focusCompleteBox()
      }

      var cancelled = false

      this.completePending = true
      myCancel = function () {
        cancelled = true
        release()
        clearState()
      }
      this.cancelPendingComplete = myCancel
      sync.holdOwned(step, ['completed'])

      this.whenCommandsIdle().then(function () {
        if (cancelled || (self._isDestroyed || self._isBeingDestroyed)) {
          return { left: false, result: null, cancelled: true }
        }

        // Every row of the step, BOTH parties' (a Service Type / Spectrum change sits on the
        // party-2 row), and the action: once the step is complete they are refused.
        var flushing = sync.flushRows(self.rowsOfStep(step.stepNo).concat([self.actionData]), { reason: 'command', ordering: { kind: 'completeStep', n: step.stepNo } })

        return waitForSaves(self.$root, flushing, { leaveLabel: copyText('requestSync.hang.cancel') })
      }).then(function (waited) {
        // Un-ticked meanwhile, or the drawer went away: nothing to ask, nothing to revert.
        if (cancelled || waited.cancelled || (self._isDestroyed || self._isBeingDestroyed) || !step.completed) {
          release()
          clearState()

          return
        }

        // Cancel at the hang dialog, or the step moved on meanwhile.
        if (waited.left || self.step !== step) {
          return stop()
        }

        // A decision of this step that did not save, or ANY save of this step's own fields
        // that did not: the failure's own section has spoken (with the "wasn't completed"
        // footer), and there is nothing honest to ask. (Superseded 2026-10-14, completed
        // steps are read-only: a failed content save of this step used to let the completion
        // through, but once the step is complete that save can never be retried.) A failure
        // of an unrelated action field (emails.*, a Site -> Actions remark) does not stop
        // it, and neither do the 428 and a path this viewer may not send.
        var failed = waited.result && waited.result.failed ? waited.result.failed : []

        if (failed.some(function (o) {
          if (o.decisionStepId !== undefined && Number(o.decisionStepId) === Number(step.id)) {
            return true
          }

          return o.status !== 'version-required' && o.status !== 'blocked' && sync.stepNoOfOutcome(o) === Number(step.stepNo)
        })) {
          return stop()
        }

        clearState()
        self.promptComplete(step, release)
      }, function (e) {
        console.error('XOL-596: flushing before Complete Step threw', e)
        stop()
      })
    },


    // The prompt of a ticked Complete Step, after the flush. `release` lets go of the
    // pre-prompt hold once the prompt is answered (completeStep takes its own).
    promptComplete (step, release) {
      var self = this

      // XOL-596 (approval lock, Carlos 2026-10-13): on a decided approval that is
      // not locked yet, the confirm becomes the three-choice prompt: Lock &
      // complete / Complete without locking / Cancel.
      if (this.completeOffersLock()) {
        var kind = this.approvalKind(step)

        approvalLock.askLock(this.$root, {
          mode: 'complete',
          stepNo: step.stepNo,
          decision: approvalLock.decisionText(kind, sync.getBaseline(step), this.actionData),
          alive: function () { return !self.syncScope.destroyed }
        }).then(function (answer) {
          if (answer === 'lock') {
            self.completeStep(true, { lock: true })
          } else if (answer === 'skip') {
            // The user has answered for this approval: closing the drawer does not
            // ask a second time.
            self.forgetDecision(step)
            self.completeStep(true)
          } else {
            self.step.completed = false
            self.focusCompleteBox()
          }

          release()
        })

        return
      }

      this.$confirm(
        copyText('requestSync.completeStep.confirm.body', { n: step.stepNo }),
        copyText('requestSync.approvalLock.complete.title'),
        {
          confirmButtonText: copyText('requestSync.completeStep.confirm.ok'),
          cancelButtonText: copyText('requestSync.completeStep.confirm.cancel'),
          type: 'warning',
          customClass: 'xol-reload-box'
        }
      ).then(() => {
        self.completeStep(true)
        release()
      }, () => {
        self.step.completed = false
        release()
      })
    },


    // Does completing THIS step ask about the lock? An approval row whose decision
    // (the baseline) is there, not locked, on a live request, for a user who may lock.
    // Everything else (Pending, steps 1 and 4, a locked step, a user who cannot lock)
    // keeps the two-button confirm.
    completeOffersLock () {
      var step = this.step

      return !!step && !!this.approvalKind(step) && this.isActive(this.actionData) && this.approvalDecided(step) &&
        !this.approvalLocked(step) && this.canLockApproval(step)
    },

    // Back to the Complete Step check-box (a Cancel of the prompt).
    focusCompleteBox () {
      var self = this

      this.$nextTick(function () {
        var el = self.$el && typeof self.$el.querySelector === 'function' ? self.$el.querySelector('.complete-step-box input, .complete-step-box') : null

        if (el && typeof el.focus === 'function') {
          el.focus()
        }
      })
    },


    // The complete / reopen command of the active step. Keys: the step and the
    // action (the response carries the derived currentStep and status).
    //
    // `opts.lock` (XOL-596 approval lock): "Lock & complete", ONE command,
    // POST /step/{id}/complete {lock: true, expected...}: the API completes and locks
    // in one transaction, or neither (the decision changed since the prompt: 409
    // stale_decision; no decision: 409 invalid_state). The pending remarks are saved
    // first and the command is not sent if that failed (the lock refuses remarks).
    completeStep (complete, opts) {
      var self = this
      var step = this.step
      var lock = !!(opts && opts.lock) && complete
      var keys = ['completed', 'completed_at', 'completed_by']
      var revert = function () {
        self.revertWorkflowFields(step, lock ? keys.concat(['approvalLocked', 'approvalLocked_by', 'approvalLocked_at']) : keys)
      }

      // The check-box is for a Tower Co manager who can write; the handler's own
      // guard, and a second toggle while a command runs. Locking also needs the lock gate.
      if (!(this.isTowerCo() && this.isManager() && this.canWriteStep()) || this.savingStep || (lock && !this.canLockApproval(step))) {
        revert()
        return Promise.resolve({ ok: false, status: 'blocked' })
      }

      // A function: the decision the user was shown is read AFTER the flush, when the
      // user's own pending decision has been saved (the prompt names it).
      var body = lock
        ? function () {
          return Object.assign({ lock: true }, approvalLock.expectedBody(self.approvalKind(step), sync.getBaseline(step)))
        }
        : {}

      // This drawer's own command: the freeze (or the re-open) it adopts is not an
      // "adopted" one (no announcement, no focus move). Cleared once the command settled.
      this.ownFreeze[step.stepNo] = true

      return this.workflowCommand({
        kind: lock ? 'completeLock' : (complete ? 'complete' : 'reopen'),
        step: step,
        rows: this.rowsOfStep(step.stepNo).concat([this.actionData]),
        url: '/step/' + step.id + (complete ? '/complete' : '/reopen'),
        body: body,
        label: complete ? 'Complete step' : 'Re-open step',
        requireSaved: lock,
        ordering: lock ? { kind: 'completeLock', n: step.stepNo } : undefined,
        hold: { row: step, keys: lock ? ['completed', 'approvalLocked'] : ['completed'] },
        verify: function (fresh) {
          var row = self.stepOfAction(fresh, step.id)

          return !!row && !!row.completed === complete && (!lock || !!row.approvalLocked)
        },
        revert: revert,
        onOk: function () {
          if (lock) {
            self.forgetDecision(step)
            savePopup.success(commandNotices.lockDoneMessage('completeLock', { step: step }).message)
          }

          self.$emit('updated', step)
        }
      }).then(function (out) {
        self.$nextTick(function () {
          delete self.ownFreeze[step.stepNo]
        })

        return out
      })
    },


    // XOL-596 (slice 3, UF5): Complete {RTx} asks first, then runs the command
    // POST /actions/{id}/complete. The confirm closes on Complete and the command
    // runs OUTSIDE it, the button reading "Completing…": Element's MessageBox is a
    // single instance with a queue, so a hang dialog (a save or the POST that
    // never answers) would queue behind a confirm kept open for the command and
    // never show. The remarks are saved first and the command is NOT sent if that
    // save fails (the ordering rule). The server completes the last step and sets
    // the status (Closed if Approved, else its decision); the drawer swaps to the
    // read-only CompletedRequest when that is adopted. (The dead `isNew` branch is
    // gone.)
    completeWorkflow () {

      // `savingStep`: a backstop for the footer's own commands (the button is disabled).
      if (!(this.isTowerCo() && this.isManager() && this.step.approved) || !this.isActive(this.actionData) || this.savingStep) {
        return Promise.resolve({ ok: false, status: 'blocked' })
      }

      var self = this
      var rtx = this.actionData.actionType
      var status = this.step.approvedStatus === 'Approved' ? 'Closed' : this.step.approvedStatus

      return new Promise(function (resolve) {
        self.$confirm(
          self.completeConfirmMessage(rtx, status),
          copyText('requestSync.complete.confirm.title', { rtx: rtx }),
          {
            confirmButtonText: copyText('requestSync.complete.confirm.ok'),
            cancelButtonText: copyText('requestSync.complete.confirm.cancel'),
            type: 'warning',
            closeOnClickModal: false,
            // centred like the "Close permanently" confirm: Element floats the status icon at the
            // vertical middle of a left-aligned body, which put it beside the bold last line
            center: true,
            dangerouslyUseHTMLString: true
          }
        ).then(function () {
          return self.runCompleteRequest(rtx).then(resolve, function (e) {
            console.error('XOL-596: completing the request threw', e)
            resolve({ ok: false, status: 'failed', err: e })
          })
        }, function () {
          // Cancel: nothing was sent.
          resolve({ ok: false, status: 'cancelled' })
        }).catch(function (e) {
          console.error('XOL-596: handling the complete confirm threw', e)
          resolve({ ok: false, status: 'failed', err: e })
        })
      })
    },


    // The body of the "Complete and close this {RTx}?" confirm. Delta 1 (item 5): it says the
    // request is closed with {status}, that no one can change it and that it can't be undone,
    // then (N7, extended from the reject confirm) that the last step's decision e-mail has not
    // been sent and can't be once the request is closed. That sentence is left out while Send
    // E-mails is off, when the e-mail has gone, and when the last step has none (the row has no
    // `approvalMail` key: only steps 2 and 5 are decorated). Every copy value is escaped
    // because the confirm renders HTML.
    completeConfirmMessage (rtx, status) {
      var html = escapeHtml(copyText('requestSync.complete.confirm.body', { rtx: rtx, status: status })) +
        '<br><br><b>' + escapeHtml(copyText('requestSync.closing.final')) + '</b>'
      var step = this.step

      if (!step || step.approvalMail !== null || this.requestMailOff()) {
        return html
      }

      var org = this.actionData.organisation
      var owner = org ? (org.name || org.shortName) : 'the owner'

      return html + '<br><br>' + escapeHtml(activityText('requestMail.complete.notSent', {
        mailStatus: step.approvedStatus,
        owner: owner,
        rtx: rtx
      }))
    },


    runCompleteRequest (rtx) {
      var self = this
      var step = this.step
      var action = this.actionData
      var target = step.approvedStatus === 'Approved' ? 'Closed' : step.approvedStatus
      var done = function (out) {
        self.completingRequest = false

        return out
      }

      this.completingRequest = true

      return this.workflowCommand({
        kind: 'completeRequest',
        step: step,
        lastStep: step,
        rows: [step, action],
        url: '/actions/' + action.id + '/complete',
        body: {},
        label: 'Complete ' + rtx,
        requireSaved: true,
        ordering: { kind: 'complete', rtx: rtx },
        verify: function (fresh) {
          return fresh.status === target
        },
        revert: function () {},
        onOk: function () {
          savePopup.success(commandNotices.doneMessage('complete', { rtx: rtx, action: action }).message)
          self.refreshStats(self.org().id, true)
        }
      }).then(done, function (e) {
        done()
        throw e
      })
    },



    stepData (current = 1) {

      if (this.rowData.steps && this.rowData.steps.length > 0) {

        if (this.activeStep) {
          current = this.activeStep
        }

        // XOL-526: was `this.isTowerCo() ? 1 : 2` — every non-Tower-Co viewer
        // (telco or provider) reads/writes party 2. viewerPartyNo() also
        // covers the single-party-workflow edge case (parties < 2).
        var partyNo = this.viewerPartyNo()

        for (var i = 0; i < this.rowData.steps.length; i++) {
          var s = this.rowData.steps[i]

          if (s.stepNo === current && s.partyNo === partyNo) {
            return s
          }

        }
      }

      return null
    },



    // XOL-534 round 3 (reviewer correction): this function is INERT, and
    // always has been, for every viewer — `this.steps` is populated only by
    // `RequestMixins.workflow()`, whose entries are
    // `{title, icon, colour, comp, data}`: no `stepNo`, no `partyNo`. The
    // `if` below therefore never matches on `main` or here. The earlier
    // comment here claimed the old `s.organisation_id === orgId` clause
    // "could never match [for a provider]" and that dropping it "fixed"
    // syncing `steps[]` — that was wrong: it never matched for ANYONE. The
    // match expression was aligned to `(stepNo, partyNo)` via
    // `viewerPartyNo()` for consistency with `stepData()` above, but this
    // has no runtime effect. Follow-up (plan §8a): delete this method and
    // its call in `stepUpdated`.
    saveStepData (stepData) {
      if (this.steps && this.steps.length > 0) {

        var stepNo = stepData.stepNo
        var partyNo = this.viewerPartyNo()

        for (var i = 0; i < this.steps.length; i++) {
          var s = this.steps[i]

          if (s.stepNo === stepNo && s.partyNo === partyNo) {
            this.steps[i] = stepData
          }

        }
      }

      return null
    },



    partyName (partyNo) {
      if (!partyNo || !this.actionData.steps || !this.actionData.steps[this.activeStep]) {
        return 'Not Found'
      } else {

        for (let i = 0; i < this.actionData.steps.length; i++) {
          let wf = this.actionData.steps[i]
          if (wf.stepNo === this.activeStep && wf.partyNo === partyNo) {
            return (wf.org.shortName ? wf.org.shortName : wf.org.name)
          }

        } // for

        return 'Not Found'
      }
    },



    partyOrg (partyNo) {
      if (!partyNo || !this.actionData.steps || !this.actionData.steps[this.activeStep]) {
        return null
      } else {

        for (let i = 0; i < this.actionData.steps.length; i++) {
          let wf = this.actionData.steps[i]
          if (wf.stepNo === this.activeStep && wf.partyNo === partyNo) {
            return wf.org
          }

        } // for

        return null
      }
    },



    titleColour () {
      switch (this.requestType) {
        case 'rta': return 'text-warning'
        case 'rts': return 'text-success'
        case 'rtu': return 'text-primary'
        case 'rtb': return 'text-info'
        case 'rtd': return 'text-danger'
        default: return 'text-info'
      }
    }

  },   // methods



  computed: {

    // XOL-596 (Complete Step box): the alert's style and its "on {date} by {name}" line follow
    // the server's word (the BASELINE), never the Complete Step v-model. The tick is HELD
    // through the flush, the prompt and the command, so a style read from `step.completed`
    // turned green, with an empty date and name, before the user confirmed anything and
    // flashed back on Cancel. Unticking to re-open is the same: the box stays "completed"
    // until the reopen is adopted. Reads `syncState.tick`, so it follows every adopt.
    completeBox: function () {
      void sync.syncState.tick

      var step = this.step

      if (!step) {
        return { done: false, at: null, by: null }
      }

      return {
        done: !!sync.baselineValue(step, 'completed'),
        at: sync.baselineValue(step, 'completed_at'),
        by: sync.baselineValue(step, 'completed_by')
      }
    },

    // XOL-596 (completed steps, CS-H): the one line above the step's form. From the
    // BASELINE (who completed it and when are the server's), never the Complete Step v-model.
    completedHint: function () {
      void sync.syncState.tick

      var n = this.activeStep

      if (!this.isTowerCo()) {
        return copyText('requestSync.completed.hint.counterparty', { towerCo: this.towerCoName() })
      }

      var p1 = this.partyOneStepRow(n)
      var base = p1 ? sync.getBaseline(p1) : {}

      return copyText(this.canReopenStep() ? 'requestSync.completed.hint.manager' : 'requestSync.completed.hint.other', {
        n: n,
        by: base.completed_by || null,
        datetime: base.completed_at ? this.formatDateTime(base.completed_at) : null
      })
    },

    // The numbers of the frozen steps, in order (the C8 watcher compares two lists).
    frozenSteps: function () {
      var out = []
      var total = this.steps ? this.steps.length : 0

      for (var n = 1; n <= total; n++) {
        if (this.isStepFrozen(n)) {
          out.push(n)
        }
      }

      return out
    },

    // XOL-596 (UX 13): what unlocks Finish Step. Only while the step is not
    // complete, the request is live and there is a next step to go to.
    finishHint: function () {
      if (!this.step || this.step.completed || !this.isActive(this.actionData) || this.activeStep >= this.steps.length) {
        return null
      }

      return this.isTowerCo() && this.isManager()
        ? copyText('requestSync.finish.hint.manager', { n: this.activeStep + 1 })
        : copyText('requestSync.finish.hint.other', { n: this.activeStep + 1 })
    },

    // True while an explicit command is in flight (here or in a step component) —
    // blocks the drawer close (DrawerBusyMixin). A decision save is not one.
    drawerBusy: function () {
      return this.busyCommands > 0
    },

    // XOL-586 (F1): Emails Update, and any view of the recipient lists, is for the
    // Tower Co only. The owner and the provider never see the popup (they used to,
    // XOL-526 / XOL-535). Whether the lists can be EDITED is the component's own
    // `canWriteStep()`; a view-only Tower Co user has the trigger disabled by Popup.
    showEmailSetup: function () {
      return this.isTowerCo()
    },

requestsInfo: function () {
      return this.info(this.rowData, this.step, this.activeStep)
    }

  },



  watch: {

    // XOL-596 (completed steps, C8): a step newly frozen or re-opened by an adopt (another
    // user's completion). A user watcher runs BEFORE the re-render, so `document.activeElement`
    // is still the control the user was in.
    frozenSteps: function (now, before) {
      var active = typeof document !== 'undefined' ? document.activeElement : null
      var self = this

      now.forEach(function (n) {
        if (before.indexOf(n) === -1) {
          self.onStepFroze(n, active)
        }
      })

      before.forEach(function (n) {
        if (now.indexOf(n) === -1) {
          self.onStepReopened(n)
        }
      })
    },

    actionData: function (value) {
      this.actionData = value
    },

    state: function (value) {
      this.state = value
    }

  },



  data () {

    return {
      show: 'workflow',
      actionData: this.rowData,
      state: this.rowData.state,
      comp: step1,
      step: this.stepData(),
      // XOL-393: the viewer's own party tab; resolved in created(). Stays a plain data
      // property (not computed) because it is a v-model target on el-tabs.
      // See docs/plans/2026-07-27-xol-393-request-render-access-defects.md (defect 2).
      selectedPartyTab: '1',
      selectedFileTab: 'fileStep' + ((this.rowData && this.rowData.currentStep) ? this.rowData.currentStep : 1),
      readOnly: (this.rowData.status === 'Closed' || this.rowData.status === 'Rejected'),
      requestType: (this.rowData.actionType ? this.rowData.actionType.toLowerCase() : 'rtx'),
      activeStep: (this.rowData && this.rowData.currentStep) ? this.rowData.currentStep : 1,
      reachedStep: (this.rowData && this.rowData.currentStep) ? this.rowData.currentStep : 1,
      fileStep: (this.rowData && this.rowData.currentStep) ? this.rowData.currentStep : 1,
      savingStep: false,
      // #id of the step-completed hint: per instance, never a duplicate id in the page.
      hintId: 'step-completed-hint-' + this._uid,
      busyCommands: 0,
      ownCommands: 0,
      completePending: false,
      completingRequest: false,
      completingLabel: copyText('requestSync.complete.busy'),
      steps: null
    }

  },



  created () {
    // Resolvers of `whenCommandsIdle`, released on teardown. Plain instance properties.
    this.idleWaiters = []
    this.cancelPendingComplete = null

    // XOL-596 (completed steps): step numbers whose freeze / re-open THIS drawer's own command
    // is bringing in (no announcement, no focus move), and the listener for record-level
    // refusals. Plain instance properties.
    this.ownFreeze = {}
    this.offStepCompleted = onStepCompleted(this.onStepCompletedRefusal, this.acceptsRefusal)

    // XOL-596: one registered object per row, on load, before anything can edit it.
    // (RequestDetails already registered them, before its e-mail auto-fill; this
    // attaches this drawer's form scope to each.)
    this.syncRegisterRow(this.actionData, 'action')

    for (var r = 0; this.actionData.steps && r < this.actionData.steps.length; r++) {
      this.syncRegisterRow(this.actionData.steps[r], 'step')
    }

    // XOL-596 (decision fields): the approval controls of steps 2, 3 and 5 are fields;
    // their groups are registered last, so this drawer's instance is the one that sends.
    this.syncRegisterDecisions(this.actionData)

    this.steps = this.workflow(this.step.partyNo, this.actionData.steps)

    // XOL-393: open on the VIEWER's own party (1 = Tower Co, 2 = the counterparty).
    // With no v-model the party tabs always opened on party 1, so a telco saw the
    // Tower Co's files and — because the panes are `lazy` — its own pane was never
    // rendered at all until clicked.
    //
    // The parties guard matters: `action_workflow.parties` can be 1 (PRG is a
    // single-party workflow), and only pane "1" exists then. Selecting "2" would match
    // no pane and el-tabs would render an empty body — a worse failure than the one
    // being fixed. CompletedRequest.vue:29 hides its tab strip outright for that case.
    this.selectedPartyTab = String(this.viewerPartyNo())

    // XOL-393: clamp to the last real step. The API parks actions.currentStep at
    // totalSteps + 1 once every party-1 step is complete (Actions::syncStatusFromSteps),
    // which made activeStep 6 of 5, stepData() return null, and the whole workflow fail
    // to render. Clamps the VIEW only — actionData.currentStep keeps the API's value,
    // which the SPA does not own.
    // See docs/plans/2026-07-27-xol-393-request-render-access-defects.md (defect 1).
    var lastStep = (this.steps && this.steps.length) ? this.steps.length : 1

    if (this.activeStep > lastStep) {
      this.activeStep = lastStep
      this.reachedStep = lastStep
      this.fileStep = lastStep
      this.selectedFileTab = 'fileStep' + lastStep
    }

    if (this.activeStep > 1) {
      this.changeStep(this.activeStep)
    }

    if (this.isNumber(this.step.approved)) {
      this.step.approved = Boolean(this.step.approved)
    }

    if (this.isNumber(this.step.completed)) {
      this.step.completed = Boolean(this.step.completed)
    }

  },


  mounted () {
    // Native typing anywhere in the drawer restarts the pause (TextInput emits to
    // its parent only on blur, so typing there changes no model).
    sync.attachScopeRoot(this.syncScope, this.$el)

    this._leaveGuard = this.leaveGuard.bind(this)
    this._leaveNeeded = this.leaveNeeded.bind(this)

    if (this.drawerDirtyRegistry && typeof this.drawerDirtyRegistry.registerLeaveGuard === 'function') {
      this.drawerDirtyRegistry.registerLeaveGuard(this._leaveGuard, this._leaveNeeded)
      // This drawer guards its own close: the global navigation guard leaves its
      // rows to it (a close is never guarded twice).
      sync.setScopeGuarded(this.syncScope, true)
    }

    window.addEventListener('focus', this.onWindowFocus)
    document.addEventListener('visibilitychange', this.onWindowFocus)
  },


  beforeDestroy () {
    // A Complete Step still waiting for a command to end lets go of its hold.
    this.idleWaiters.slice().forEach(function (w) { w() })

    window.removeEventListener('focus', this.onWindowFocus)
    document.removeEventListener('visibilitychange', this.onWindowFocus)

    if (this.offStepCompleted) {
      this.offStepCompleted()
    }

    if (this.drawerDirtyRegistry && typeof this.drawerDirtyRegistry.unregisterLeaveGuard === 'function') {
      this.drawerDirtyRegistry.unregisterLeaveGuard(this._leaveGuard)
      sync.setScopeGuarded(this.syncScope, false)
    }

    // The safety flush (R2.14): stop the timer and save whatever changed now. A
    // field whose save fails on the network keeps its record, so it stays pending.
    sync.destroyScope(this.syncScope)
  }

}

</script>

<style scoped>
  .el-select {display: inline-block; width: 100%;}

  /* XOL-586: the footer links (Activity, then Emails Update), right-aligned on one line. */
  .request-footer-links {display: flex; justify-content: flex-end; align-items: center; flex-wrap: wrap;}
  .request-footer-links > div {margin-left: 16px;}

  /* XOL-596 (completed steps): the hint takes focus when a freeze disabled the control the
     user was in (palette colour only). */
  .step-completed-hint:focus {outline: 2px solid #409EFF; outline-offset: 2px;}
  /* Carlos, 2026-10-15: room between the hint and the step's first section (step 1's
     General Remarks, step 4's Project/Planner Dept sat right under it). */
  .step-completed-hint {margin-bottom: 24px;}

  /* XOL-607: the step footer, in DOM order = visual order = focus order. No cell sets
     position, z-index, pointer-events or an offset: nothing can paint over the Complete Step
     box. The side cells keep min-width: auto, so a wide centre box shrinks and wraps its text
     instead of the side cells collapsing to 0 under it. */
  /* Carlos, 2026-10-15: room between the step's last block and the footer (step 2's QR-code
     band and step 3's collapse border sat flush on it). */
  .workflow-footer {display: flex; align-items: flex-start; gap: 16px; margin-top: 24px;}
  .workflow-footer__prev,
  .workflow-footer__finish {flex: 1 1 0;}
  .workflow-footer__prev {text-align: left;}
  .workflow-footer__complete {flex: 0 1 auto; min-width: 0; overflow-wrap: anywhere;}
  .workflow-footer__finish {display: flex; flex-wrap: wrap; justify-content: flex-end; align-items: center; gap: 8px;}
  .workflow-footer__finish .el-button + .el-button {margin-left: 0;}
  .workflow-footer__hint {flex: 0 0 100%; text-align: right;}

  /* xs / sm / md (vue-mq keys are upper bounds: a 375px phone is sm): Previous, the Complete
     box, then Finish, one under the other. */
  .workflow-footer--stacked {flex-direction: column; align-items: stretch; gap: 12px; margin-top: 16px;}
  .workflow-footer--stacked .workflow-footer__prev,
  .workflow-footer--stacked .workflow-footer__finish {flex: 0 0 auto;}
  .workflow-footer--stacked .workflow-footer__prev--empty {display: none;}

</style>
